import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import { useRollbar } from '@rollbar/react';
import classNames from 'classnames';
import * as EmailValidator from 'email-validator';
import { useState } from 'react';
import { InputType } from '@/components/BaseLibrary/FormInput/FormInput';
import Button, { ButtonVariant } from '@/components/OldButton/OldButton';
import RichText from '@/components/RichText/RichText';
import { getBaseUrlByEnv } from '@/constants/baseUrls';
import type { Contentful } from '@/contentful/contentful-exports';
import useEligibility, { EligibilityType } from '@/hooks/useEligibility';
import {
  type LeadFormInputData,
  ReferralValue,
  useSubmitLeadToZeus,
} from '@/hooks/useSubmitLeadToZeus';
import { getErrorMessage, getErrorObject } from '@/utils/getErrorMessage';
import { PHONE_REGEX, ZIP_REGEX } from '@/utils/validation/constants';
import { OwnHome } from '@/utils/validation/inputs';
import Input from './Input';
import useStyles from './TaxForm.styles';

/**
 * @deprecated V2 component
 */
export interface TaxFormContent extends Contentful {
  fields: {
    title: string;
    subTitle: string;
    tos: RichTextDocument;
  };
}

interface Props {
  content: TaxFormContent;
}

const billAmounts: string[] = [
  '',
  '$0-50',
  '$51-100',
  '$101-150',
  '$151-200',
  '$201-300',
  '$301-400',
  '$401-500',
  '$501-600',
  '$601-700',
  '$701-800',
  '$801+',
];

type SelectProps = {
  value: string;
  handleChange: (value: string) => void;
  classes?: string;
  placeholder?: string;
};

export const Select = ({
  value,
  handleChange,
  classes,
  placeholder,
}: SelectProps) => {
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    handleChange(event.target.value);
  };

  return (
    <select value={value} onChange={handleSelectChange} className={classes}>
      {billAmounts.map((billAmount) => (
        <option key={billAmount} value={billAmount}>
          {billAmount || placeholder || 'Select Monthly Bill Amount'}
        </option>
      ))}
    </select>
  );
};

export const handleChange =
  (setStateFn: (value: string) => void) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    setStateFn(e.target.value);
  };

export const getStepError = (
  value?: string,
  regex?: RegExp
): string | undefined => {
  return value && regex && !regex.test(value) ? 'Invalid value' : undefined;
};

export const handleCheckboxChange = (
  setValue: (value: boolean) => void,
  value: boolean
) => {
  return () => {
    setValue(!value);
  };
};

export default function TaxForm({ content }: Props) {
  const { classes } = useStyles();

  const { title, subTitle, tos } = content.fields;

  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
  const [ownHome, setOwnHome] = useState<OwnHome>(OwnHome.OWN);
  const [zipCode, setZipCode] = useState<string>('');
  const [averageElectricBill, setAverageElectricBill] = useState('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [interestedInBattery, setInterestedInBattery] =
    useState<boolean>(false);
  const [wasReferred, setWasReferred] = useState<boolean>(false);
  const [isZipInService, setIsZipInService] = useState<boolean | null>(true);
  const [networkError, setNetworkError] = useState<string>('');
  const [isLeadFormSubmitted, setIsLeadFormSubmitted] =
    useState<boolean>(false);

  const { submitLeadToZeus } = useSubmitLeadToZeus('Embedded Lead Form');
  const { fetchEligibility } = useEligibility();
  const rollbar = useRollbar();

  const handleOwnHomeRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOwnHome(e.target.value as OwnHome);
  };

  const handleSubmit = async (): Promise<void> => {
    const zeusData: LeadFormInputData = {
      first_name: firstName,
      last_name: lastName,
      email,
      zip: zipCode,
      phone_home: phone,
      property_ownership: ownHome,
      electric_bill: averageElectricBill,
      ...(wasReferred && {
        Referral_web_form__c: ReferralValue.YES,
      }),
      ...(interestedInBattery && {
        additionalServices: 'Energy+Storage+/+Bright+Box+Battery',
      }),
    };
    try {
      setIsZipInService(fetchEligibility(EligibilityType.WEBSITE, zipCode));
      await submitLeadToZeus({
        zeusData,
        honeypot: Boolean(address),
      });
      setNetworkError('');
      setIsLeadFormSubmitted(true);
    } catch (err: unknown) {
      rollbar.error(getErrorMessage(err), getErrorObject(err));
      setNetworkError(
        'There was an error submitting the form, please try again'
      );
    }
  };
  interface Step {
    stepNumber: number;
    formContent: React.JSX.Element;
    shouldRender: boolean;
  }

  type Steps = Array<Step>;

  const steps: Steps = [
    {
      stepNumber: 1,
      formContent: (
        <>
          {/** biome-ignore lint/a11y/noLabelWithoutControl: Automated migration to Biome */}
          <label className={classes.formLabel}>Do you own your own home?</label>
          <div className={classes.radioRow}>
            <input
              type='radio'
              value={OwnHome.OWN}
              checked={ownHome === OwnHome.OWN}
              onChange={handleOwnHomeRadioChange}
            />
            <p className={classes.radioLabel}>Yes</p>
          </div>
          <div className={classes.radioRow}>
            <input
              type='radio'
              value={OwnHome.RENT}
              checked={ownHome === OwnHome.RENT}
              onChange={handleOwnHomeRadioChange}
            />
            <p className={classes.radioLabel}>No, I rent my home</p>
          </div>
          <Input
            value={zipCode}
            onChange={handleChange(setZipCode)}
            label='Zip code'
            error={getStepError(zipCode, ZIP_REGEX)}
            type={InputType.NUMBER}
            inputMode='numeric'
          />
          <Button
            onClick={() => setCurrentStep(2)}
            variant={ButtonVariant.PrimaryBluePill}
            label='Get Started'
            disabled={!(zipCode && zipCode.length === 5)}
          />
        </>
      ),
      shouldRender: currentStep === 1,
    },
    {
      stepNumber: 2,
      formContent: (
        <>
          {/** biome-ignore lint/a11y/noLabelWithoutControl: Automated migration to Biome */}
          <label className={classes.formLabel}>
            What is your average monthly electric bill?
          </label>
          {/** biome-ignore lint/a11y/noLabelWithoutControl: Automated migration to Biome */}
          <label className={classes.formLabel}>Avg Monthly Electric Bill</label>
          <Select
            value={averageElectricBill}
            handleChange={setAverageElectricBill}
            classes={classes.select}
          />
          <Button
            onClick={() => setCurrentStep(3)}
            variant={ButtonVariant.PrimaryBluePill}
            label='Next'
            disabled={!averageElectricBill}
          />
        </>
      ),
      shouldRender: currentStep === 2,
    },
    {
      stepNumber: 3,
      formContent: (
        <>
          <Input
            value={firstName}
            onChange={handleChange(setFirstName)}
            label='First Name'
            error={getStepError(firstName)}
          />
          <Input
            value={lastName}
            onChange={handleChange(setLastName)}
            label='Last Name'
            error={getStepError(lastName)}
          />
          <Input
            value={phone}
            onChange={handleChange(setPhone)}
            label='Phone Number'
            error={getStepError(phone, PHONE_REGEX)}
            type={InputType.NUMBER}
            inputMode='numeric'
          />
          <div className={classes.hidden}>
            <Input
              value={address}
              onChange={handleChange(setAddress)}
              label='Address'
              type={InputType.ADDRESS}
              inputMode='text'
              autocomplete='off'
            />
          </div>
          <Button
            onClick={() => setCurrentStep(4)}
            variant={ButtonVariant.PrimaryBluePill}
            label='Next'
            disabled={
              !(firstName && lastName && phone) ||
              !!getStepError(phone, PHONE_REGEX)
            }
          />
        </>
      ),
      shouldRender: currentStep === 3,
    },
    {
      stepNumber: 4,
      formContent: (
        <>
          <Input
            value={email}
            onChange={handleChange(setEmail)}
            label='Email Address'
            error={EmailValidator.validate(email) ? 'Invalid value' : undefined}
          />
          <div className={classes.checkboxRow}>
            <input
              type='checkbox'
              checked={interestedInBattery}
              onChange={handleCheckboxChange(
                setInterestedInBattery,
                interestedInBattery
              )}
            ></input>
            <p className={classes.checkboxLabel}>
              I&apos;m interested in Brightbox Solar Battery Storage
            </p>
          </div>
          <div className={classes.checkboxRow}>
            <input
              type='checkbox'
              checked={wasReferred}
              onChange={handleCheckboxChange(setWasReferred, wasReferred)}
            ></input>
            <p className={classes.checkboxLabel}>
              I was referred to Sunrun by a friend or family member
            </p>
          </div>
          <div className={classes.tos}>
            <label>
              {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
              <input
                type='hidden'
                id='leadid_tcpa_disclosure'
                name='Terms of Service'
              />
              <RichText
                content={tos}
                className={classes.tos}
                title={content.fields?.title}
              />
            </label>
          </div>
          <Button
            onClick={handleSubmit}
            variant={ButtonVariant.PrimaryBluePill}
            label='See if you qualify'
            disabled={!email || !EmailValidator.validate(email)}
          />
          <p className={classes.error}>{networkError}</p>
        </>
      ),
      shouldRender: currentStep === 4,
    },
  ];

  const renderLeadFormSubmission = (): React.JSX.Element | null => {
    if (!isLeadFormSubmitted) {
      return null;
    }
    return (
      <div>
        <div className={classes.submissionContent}>
          <h3 className={classes.submissionTitle}>
            {isZipInService
              ? `Thanks so much! We'll call you shortly.`
              : `Sorry, we are not taking new customers in your area yet`}
          </h3>
          <Button
            url={`${getBaseUrlByEnv()}/referral`}
            label='Refer a friend. Get $1000'
          />
        </div>
        {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
        <img
          src={'/cmsAssets/assets/img/EmbeddedLeadSubmission.png'}
          className={classes.submissionImage}
          alt='submission form'
        />
      </div>
    );
  };

  const renderSubmitted = (): React.JSX.Element | null => {
    if (!isLeadFormSubmitted) {
      return (
        <>
          <h3 className={classes.title}>{title}</h3>
          <h4 className={classNames([classes.subtitle])}>{subTitle}</h4>
          <div className={classes.progressCircleSection}>
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={classNames([
                  classes.progressCircle,
                  currentStep >= step && classes.progressCircleSelected,
                ])}
              >
                {step}
              </div>
            ))}
          </div>
        </>
      );
    }
    return null;
  };

  const renderStep = (step: Step) => {
    if (!step.shouldRender) {
      return null;
    }
    return (
      // biome-ignore lint/style/useTemplate: Automated migration to Biome
      <form className={classes.form} key={'step-' + step.stepNumber}>
        {step.formContent}
      </form>
    );
  };

  return (
    <div
      className={classes.embeddedLeadForm}
      style={isLeadFormSubmitted ? { backgroundColor: '#F2F2F2' } : {}}
    >
      {renderLeadFormSubmission()}
      {renderSubmitted()}
      {!isLeadFormSubmitted && steps.map(renderStep)}
    </div>
  );
}
